import { useState, useEffect } from 'react';
import { apiCall } from '../api/client';
import styles from './SendDialog.module.css';

interface Destination {
  destId: number;
  name: string;
  aeTitle: string;
  host: string;
  port: number;
}

interface Props {
  studyId: number;
  studyDescription?: string;
  patientName?: string;
  onClose: () => void;
  onJobCreated: (jobId: number) => void;
}

export default function SendDialog({
  studyId, studyDescription, patientName, onClose, onJobCreated,
}: Props) {
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [selectedDest, setSelectedDest] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      try {
        const res = await apiCall<Destination>('destinations.list');
        if (res.s === 'ok') {
          setDestinations(res.data || []);
          if (res.data?.[0]) setSelectedDest(res.data[0].destId);
        } else {
          setError(res.error || 'Unable to load destinations.');
        }
      } catch {
        setError('Unable to reach the server.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleSend() {
    if (!selectedDest) return;
    setSending(true);
    setError('');

    try {
      const res = await apiCall<{ jobId: number }>('study.send', {
        studyId,
        seriesId: 0, // entire study
        destId: selectedDest,
      });
      if (res.s === 'ok' && res.data?.[0]) {
        onJobCreated(res.data[0].jobId);
        onClose();
      } else {
        setError(res.error || 'Unable to create send job.');
      }
    } catch {
      setError('Unable to reach the server.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <h3>Send Study</h3>

        <div className={styles.studyInfo}>
          <div className={styles.studyName}>{patientName}</div>
          <div className={styles.studyDesc}>{studyDescription}</div>
        </div>

        {loading ? (
          <div className={styles.message}>Loading destinations...</div>
        ) : destinations.length === 0 ? (
          <div className={styles.message}>
            No destinations configured. Open Settings (gear icon, top right of
            Worklist) to add a PACS or workstation.
          </div>
        ) : (
          <div className={styles.destList}>
            <label className={styles.label}>Send to:</label>
            {destinations.map((d) => (
              <label
                key={d.destId}
                className={`${styles.destItem} ${selectedDest === d.destId ? styles.destSelected : ''}`}
              >
                <input
                  type="radio"
                  name="dest"
                  checked={selectedDest === d.destId}
                  onChange={() => setSelectedDest(d.destId)}
                />
                <div className={styles.destInfo}>
                  <div className={styles.destName}>{d.name}</div>
                  <div className={styles.destMeta}>
                    {d.aeTitle} @ {d.host}:{d.port}
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actions}>
          <button onClick={onClose} className={styles.cancelBtn}>Cancel</button>
          <button
            onClick={handleSend}
            disabled={sending || !selectedDest || loading}
          >
            {sending ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
